import { Injectable } from '@angular/core';

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  mimeType?: 'image/webp' | 'image/jpeg' | 'image/png';
  preserveAlpha?: boolean;
}

export interface ImageVariantSpec extends CompressionOptions {
  key: 'icon128' | 'thumb160' | 'card384' | 'tile768' | 'hero1200';
  width: number;
}

export interface GeneratedImageVariant {
  key: ImageVariantSpec['key'];
  file: File;
  width: number;
  height: number;
  hasAlpha: boolean;
}

const extensionForMime = (mime: string) =>
  mime === 'image/jpeg' ? 'jpg' : mime.split('/')[1] || 'webp';

export const chooseImageOutputMime = (
  hasAlpha: boolean,
  requested?: CompressionOptions['mimeType'],
) => {
  if (hasAlpha) return requested === 'image/png' ? 'image/png' : 'image/webp';
  return requested === 'image/jpeg' ? 'image/jpeg' : 'image/webp';
};

export const getScaledImageDimensions = (
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number,
) => {
  const scale = Math.min(1, maxWidth / width, maxHeight / height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

@Injectable({ providedIn: 'root' })
export class ImageCompressionService {
  private loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(image);
      };
      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Unable to decode image'));
      };
      image.src = objectUrl;
    });
  }

  private detectAlpha(image: HTMLImageElement): boolean {
    const canvas = document.createElement('canvas');
    const scale = Math.min(
      1,
      256 / Math.max(image.naturalWidth, image.naturalHeight),
    );
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return false;
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
    for (let index = 3; index < pixels.length; index += 4) {
      if (pixels[index] < 255) return true;
    }
    return false;
  }

  private render(
    image: HTMLImageElement,
    fileName: string,
    hasAlpha: boolean,
    options: CompressionOptions,
  ): Promise<{ file: File; width: number; height: number }> {
    const maxWidth = options.maxWidth ?? 1200;
    const maxHeight = options.maxHeight ?? 1200;
    const { width, height } = getScaledImageDimensions(
      image.naturalWidth,
      image.naturalHeight,
      maxWidth,
      maxHeight,
    );
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return Promise.reject(new Error('Canvas is unavailable'));
    context.clearRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    const mimeType = chooseImageOutputMime(
      options.preserveAlpha === false ? false : hasAlpha,
      options.mimeType,
    );
    const quality = mimeType === 'image/png' ? undefined : options.quality ?? 0.82;
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error('Unable to encode image'));
            return;
          }
          const outputName = fileName.replace(
            /\.[^/.]+$/,
            `.${extensionForMime(mimeType)}`,
          );
          resolve({
            file: new File([blob], outputName, {
              type: mimeType,
              lastModified: Date.now(),
            }),
            width,
            height,
          });
        },
        mimeType,
        quality,
      );
    });
  }

  async inspect(file: File) {
    if (!file.type.startsWith('image/')) {
      throw new Error('Only image files can be processed');
    }
    const image = await this.loadImage(file);
    return {
      image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      hasAlpha: this.detectAlpha(image),
    };
  }

  async compress(file: File, options: CompressionOptions = {}): Promise<File> {
    if (!file.type.startsWith('image/')) return file;
    const { image, hasAlpha } = await this.inspect(file);
    return (await this.render(image, file.name, hasAlpha, options)).file;
  }

  async generateVariants(file: File, specs: ImageVariantSpec[]) {
    const { image, hasAlpha, width, height } = await this.inspect(file);
    const master = await this.render(image, file.name, hasAlpha, {
      maxWidth: 3000,
      maxHeight: 3000,
      quality: 0.9,
    });
    const variants: GeneratedImageVariant[] = [];
    for (const spec of specs) {
      const rendered = await this.render(image, `${spec.key}-${file.name}`, hasAlpha, {
        ...spec,
        maxWidth: spec.width,
        maxHeight: spec.maxHeight ?? spec.width * 2,
      });
      variants.push({ key: spec.key, ...rendered, hasAlpha });
    }
    return {
      master: { ...master, hasAlpha },
      variants,
      original: { width, height, mimeType: file.type, size: file.size },
    };
  }
}
